import { apiFetch } from '../../shared/apiClient';

function apiUrl(path: string): string {
	const isLocalhost =
		window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	return isLocalhost ? path : `${import.meta.env.VITE_PRODUCT_SERVER}${path}`;
}

export type ExternalDataLayer = 'SCHEDULE' | 'ODDS' | 'LIVE' | 'FULL_MATCH' | 'STANDINGS';
export type MonitoringStatus = 'SUCCESS' | 'PARTIAL' | 'FAILED' | 'SKIPPED';
export type MonitoringTrigger = 'CRON' | 'ADMIN' | 'ORCHESTRATOR';

export type MonitoringCounters = {
	requested?: number | null;
	upserted?: number | null;
	updated?: number | null;
	matched?: number | null;
	saved?: number | null;
	skipped?: number | null;
	skippedFar?: number | null;
	skippedNoBookieEvent?: number | null;
	skippedMissingKickoff?: number | null;
	mappingFailures?: number | null;
	sseCalls?: number | null;
	roundsParsed?: number | null;
	finishedDetected?: number | null;
	tournamentFetched?: boolean | null;
	eligible?: number | null;
};

export type MonitoringHttpLog = {
	requestType?: string | null;
	target?: string | null;
	teams?: string | null;
	homeTitle?: string | null;
	awayTitle?: string | null;
	homeLogoKey?: string | null;
	awayLogoKey?: string | null;
	httpStatus?: number | null;
	outcome?: string | null;
	durationMs?: number;
	detail?: string | null;
	retryAfterSeconds?: number | null;
	requestedAt?: string | null;
};

export type MonitoringFailedMatch = {
	matchScheduleId?: string | null;
	homeTitle?: string | null;
	awayTitle?: string | null;
	homeLogoKey?: string | null;
	awayLogoKey?: string | null;
};

export type MonitoringRun = {
	id: string;
	layer: ExternalDataLayer;
	provider?: string | null;
	trigger?: MonitoringTrigger | null;
	status?: MonitoringStatus | null;
	startedAt?: string | null;
	finishedAt?: string | null;
	durationMs?: number | null;
	leagueCode?: string | null;
	season?: string | null;
	matchday?: number | null;
	slotOrders?: number[] | null;
	slotScope?: string | null;
	manual?: boolean;
	counters?: MonitoringCounters | null;
	httpRequestsTotal?: number;
	httpRequestsFailed?: number;
	httpLogs?: MonitoringHttpLog[];
	errorSummary?: string | null;
	failedMatchScheduleIds?: string[] | null;
	failedMatchLabels?: string[] | null;
	failedMatches?: MonitoringFailedMatch[] | null;
	failoverUsed?: boolean;
};

export const MONITORING_LAYERS: ExternalDataLayer[] = [
	'SCHEDULE',
	'ODDS',
	'LIVE',
	'FULL_MATCH',
	'STANDINGS',
];

export type MonitoringLayerPage = {
	runs: MonitoringRun[];
	total: number;
	failed: number;
	offset?: number;
	limit?: number;
	hasMore?: boolean;
};

export type MonitoringRunsQuery = {
	hours?: number;
	limit?: number;
	offset?: number;
	/** Comma-separated statuses or `issues` (FAILED+PARTIAL). */
	status?: string;
};

export async function fetchMonitoringRuns(
	layer: ExternalDataLayer,
	query: MonitoringRunsQuery = {}
): Promise<MonitoringLayerPage> {
	const hours = query.hours ?? 24;
	const limit = query.limit ?? 50;
	const offset = query.offset ?? 0;
	const params = new URLSearchParams({
		layer,
		hours: String(hours),
		limit: String(limit),
		offset: String(offset),
	});
	if (query.status) {
		params.set('status', query.status);
	}
	const result = await apiFetch(apiUrl(`/api/admin/external-api-monitoring?${params}`));
	if (result.status >= 400) {
		const { message } = await result.json();
		throw new Error(message);
	}
	const body: MonitoringLayerPage = await result.json();
	return {
		runs: body.runs ?? [],
		total: body.total ?? 0,
		failed: body.failed ?? 0,
		offset: body.offset ?? offset,
		limit: body.limit ?? limit,
		hasMore: body.hasMore ?? false,
	};
}

export async function fetchMonitoringLatest(): Promise<Partial<Record<ExternalDataLayer, MonitoringRun>>> {
	const result = await apiFetch(apiUrl('/api/admin/external-api-monitoring/latest'));
	if (result.status >= 400) {
		const { message } = await result.json();
		throw new Error(message);
	}
	return result.json();
}

export async function fetchMonitoringRun(id: string): Promise<MonitoringRun> {
	const result = await apiFetch(apiUrl(`/api/admin/external-api-monitoring/${encodeURIComponent(id)}`));
	if (result.status >= 400) {
		const { message } = await result.json();
		throw new Error(message);
	}
	return result.json();
}

export async function deleteMonitoringRunsByLayer(layer: ExternalDataLayer): Promise<number> {
	const params = new URLSearchParams({ layer });
	const result = await apiFetch(apiUrl(`/api/admin/external-api-monitoring?${params}`), {
		method: 'DELETE',
	});
	if (result.status >= 400) {
		const { message } = await result.json();
		throw new Error(message);
	}
	const body: { deleted?: number } = await result.json();
	return body.deleted ?? 0;
}
